import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import {
  useProposalQuoteContractSummary,
  QUOTE_STATUS_LABELS,
  CONTRACT_STATUS_LABELS,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  canSeeQuoteContractMenu,
  hasPermission,
} from '@/lib/quoteContractPermissions';

interface Props {
  proposalId: string;
  proposalVersionId?: string;
  proposalStatus?: string;
}

export default function ProposalQuoteContractPanel({
  proposalId,
  proposalVersionId,
  proposalStatus,
}: Props) {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const canCreate = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CREATE,
  );
  const { summary, loading, error, fetchSummary } =
    useProposalQuoteContractSummary();

  useEffect(() => {
    if (canRead && proposalId) void fetchSummary(proposalId);
  }, [canRead, proposalId, fetchSummary]);

  if (!canRead) return null;

  const proposalReady =
    proposalStatus === 'approved' || proposalStatus === 'locked';

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">关联报价 / 合同</h3>
        {canCreate && proposalReady && proposalVersionId && (
          <Button
            size="sm"
            onClick={() =>
              navigate(
                `/quote-contract/quotes/create?proposalId=${encodeURIComponent(proposalId)}&proposalVersionId=${encodeURIComponent(proposalVersionId)}`,
              )
            }
          >
            基于该方案版本新建报价
          </Button>
        )}
      </div>
      {!proposalReady && (
        <p className="text-xs text-muted-foreground">
          方案必须先通过审核或锁定，才能生成正式报价。
        </p>
      )}
      {loading && <p className="text-xs text-muted-foreground">加载中…</p>}
      {error && (
        <p className="text-xs text-destructive">加载失败：{error.message}</p>
      )}
      {summary && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-md border p-3">
              <div className="text-xs font-medium">最新已审核报价</div>
              {summary.latestApprovedQuote ? (
                <div className="mt-1 space-y-1 text-xs">
                  <div className="font-mono">
                    {summary.latestApprovedQuote.quoteNo}
                  </div>
                  <div>{summary.latestApprovedQuote.title}</div>
                  <div className="text-muted-foreground">
                    {QUOTE_STATUS_LABELS[summary.latestApprovedQuote.status]} ·{' '}
                    {summary.latestApprovedQuote.currency}{' '}
                    {summary.latestApprovedQuote.totalAmount}
                  </div>
                  <button
                    className="text-xs text-primary hover:underline"
                    onClick={() =>
                      navigate(
                        `/quote-contract/quotes/${summary.latestApprovedQuote!.id}`,
                      )
                    }
                  >
                    打开报价 →
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  暂无已审核通过的报价
                </p>
              )}
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs font-medium">最新合同</div>
              {summary.latestContract ? (
                <div className="mt-1 space-y-1 text-xs">
                  <div className="font-mono">
                    {summary.latestContract.contractNo}
                  </div>
                  <div>{summary.latestContract.title}</div>
                  <div className="text-muted-foreground">
                    {CONTRACT_STATUS_LABELS[summary.latestContract.status]} ·{' '}
                    {summary.latestContract.currency}{' '}
                    {summary.latestContract.effectiveTotalAmount}
                  </div>
                  <button
                    className="text-xs text-primary hover:underline"
                    onClick={() =>
                      navigate(
                        `/quote-contract/contracts/${summary.latestContract!.id}`,
                      )
                    }
                  >
                    打开合同 →
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">暂无合同</p>
              )}
            </div>
          </div>
          {summary.quotes.length > 0 && (
            <div>
              <h4 className="text-xs font-medium">该方案历史报价</h4>
              <ul className="mt-1 space-y-1 text-xs">
                {summary.quotes.map((q) => (
                  <li
                    key={q.id}
                    className="flex items-center justify-between rounded-md border px-2 py-1"
                  >
                    <span>
                      <span className="font-mono">{q.quoteNo}</span> · {q.title}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px]">
                        {QUOTE_STATUS_LABELS[q.status]}
                      </span>
                      <button
                        className="text-primary hover:underline"
                        onClick={() =>
                          navigate(`/quote-contract/quotes/${q.id}`)
                        }
                      >
                        打开
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
